import {FieldCombobox, FieldInput, Icon, NationalIdField} from '@sollapay/ui/components';
import {getCountryOptions} from '@sollapay/utils';
import {Controller} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';

import type {StringFieldRule} from '@/lib/validation';
import type {ReactNode} from 'react';
import type {Control, ControllerProps, FieldPath, FieldValues} from 'react-hook-form';

export interface IdentityDocumentFieldsProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  isPassport: boolean;
  idNumberName: FieldPath<TFieldValues>;
  idCountryName: FieldPath<TFieldValues>;
  idNumberRules?: StringFieldRule;
  idCountryRules?: Pick<StringFieldRule, 'required'>;
  idNumberError?: string;
  idCountryError?: string;
  cornerBadge?: ReactNode;
  isLoading?: boolean;
  onIdNumberChange?: () => void;
  idCountryCornerBadge?: ReactNode;
  idCountryIsLoading?: boolean;
  onIdCountryChange?: () => void;
  isLocked?: boolean;
  lockedTooltip?: ReactNode;
}

/** The caller's wrapper takes `identityFieldsCellClassName` so a passport can span the row. */
export function IdentityDocumentFields<TFieldValues extends FieldValues>({
  control,
  isPassport,
  idNumberName,
  idCountryName,
  idNumberRules,
  idCountryRules,
  idNumberError,
  idCountryError,
  cornerBadge,
  isLoading,
  onIdNumberChange,
  idCountryCornerBadge,
  idCountryIsLoading,
  onIdCountryChange,
  isLocked,
  lockedTooltip
}: IdentityDocumentFieldsProps<TFieldValues>) {
  const {t, i18n} = useTranslation('common');
  const countryOptions = getCountryOptions(i18n.language);

  return (
    <>
      <Controller
        name={idNumberName}
        control={control}
        rules={idNumberRules as ControllerProps<TFieldValues>['rules']}
        render={({field}) => {
          const sharedProps = {
            error: idNumberError,
            required: true,
            isLocked,
            lockedTooltip,
            cornerBadge,
            isLoading,
            value: field.value as string,
            onBlur: field.onBlur
          };
          return isPassport ? (
            <FieldInput
              {...sharedProps}
              label={t('identity.passportLabel')}
              placeholder={t('identity.passportPlaceholder')}
              description={
                <Trans
                  t={t}
                  i18nKey="identity.passportDescription"
                  components={{range: <bdi dir="ltr" />}}
                />
              }
              startAdornment={<Icon name="passport" className="size-4" />}
              onChange={e => {
                field.onChange(e.target.value);
                onIdNumberChange?.();
              }}
            />
          ) : (
            <NationalIdField
              {...sharedProps}
              label={t('identity.nationalIdLabel')}
              placeholder={t('identity.nationalIdPlaceholder')}
              onChange={value => {
                field.onChange(value);
                onIdNumberChange?.();
              }}
            />
          );
        }}
      />

      {isPassport && (
        <Controller
          name={idCountryName}
          control={control}
          rules={idCountryRules as ControllerProps<TFieldValues>['rules']}
          render={({field}) => (
            <FieldCombobox
              label={t('identity.country')}
              placeholder={t('identity.countryPlaceholder')}
              startAdornment={<Icon name="globe" className="size-4" />}
              error={idCountryError}
              required
              isLocked={isLocked}
              lockedTooltip={lockedTooltip}
              options={countryOptions}
              value={field.value as string}
              cornerBadge={idCountryCornerBadge}
              loading={idCountryIsLoading}
              onValueChange={value => {
                field.onChange(value);
                onIdCountryChange?.();
              }}
            />
          )}
        />
      )}
    </>
  );
}

/** Passport spans the row so number and issuing country sit side by side. */
export function identityFieldsCellClassName(isPassport: boolean): string | undefined {
  return isPassport ? 'col-span-2 grid grid-cols-2 gap-6' : undefined;
}

/** The name field takes the full row whenever the passport pair needs a row of its own. */
export function identityNameCellClassName(isPassport: boolean): string | undefined {
  return isPassport ? 'col-span-2' : undefined;
}
